import { useEffect, useState } from 'react';
import Modal from '../../../components/Modal';
import { toast } from 'react-toastify';
import { TOAST_CONFIG } from '../../../config/constants';
import { authAxios } from '../../../helpers/axios';
import { updateEnquery } from '../../../components/services';

const LinkStudentModal = ({ enquiryId, customerId, parentName, defaultStandard, setModal, onSuccess }) => {
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState('');
  const [addNewStudent, setAddNewStudent] = useState(false);
  const [newStudent, setNewStudent] = useState({ firstName: '', lastName: '', standard: defaultStandard || '', gender: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const res = await authAxios.get(`/customer/student/${customerId}`);
        setStudents(res.data.students || []);
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Failed to load students', TOAST_CONFIG);
      }
    };
    if (customerId) fetchStudents();
  }, [customerId]);

  const _onSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      let payload = { _id: enquiryId };

      if (!addNewStudent && selectedStudent) {
        payload.student = selectedStudent;
      } else if (addNewStudent) {
        const resolvedStandard = newStudent.standard || defaultStandard || '';
        if (!newStudent.firstName || !resolvedStandard || !newStudent.gender) {
          toast.error('Please fill student name, class and gender', TOAST_CONFIG);
          setLoading(false);
          return;
        }
        payload.student = {
          firstName: newStudent.firstName,
          lastName: newStudent.lastName,
          class: resolvedStandard,
          gender: newStudent.gender,
        };
      } else {
        toast.error('Please select or create a student', TOAST_CONFIG);
        setLoading(false);
        return;
      }

      const res = await updateEnquery(payload);
      toast.success('Student linked successfully', TOAST_CONFIG);
      onSuccess?.(res);
      setModal(false);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to link student', TOAST_CONFIG);
      setLoading(false);
    }
  };

  return (
    <Modal title="Link Student" size="md">
      <form onSubmit={_onSubmit}>
        <div className="modal-inner-content">
          {parentName ? (
            <p className="xxs-text m-weight h-op" style={{ marginBottom: 16 }}>
              Parent: <span className="f-op caps">{parentName}</span>
            </p>
          ) : null}

          {!addNewStudent ? (
            <div>
              <label>Select Student</label>
              <select
                className="text-input"
                value={selectedStudent}
                onChange={(e) => {
                  const id = e.target.value;
                  setSelectedStudent(id);
                  const student = students.find((s) => s._id === id);
                  if (student?.class) {
                    setNewStudent((prev) => ({ ...prev, standard: student.class }));
                  }
                }}
              >
                <option value="">Select Student</option>
                {students.map((s) => (
                  <option key={s._id} value={s._id}>
                    {s.firstName} {s.lastName} {s.class ? `- ${s.class}` : ''}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="tertiary-btn"
                style={{ paddingLeft: 0 }}
                onClick={() => {
                  setAddNewStudent(true);
                  setSelectedStudent('');
                }}
              >
                Add new student
              </button>
            </div>
          ) : (
            <>
              <div className="input-group-divided">
                <div>
                  <label>Student First Name*</label>
                  <input
                    type="text"
                    className="text-input"
                    value={newStudent.firstName}
                    required
                    onChange={(e) => setNewStudent((prev) => ({ ...prev, firstName: e.target.value }))}
                  />
                </div>
                <div>
                  <label>Student Last Name</label>
                  <input
                    type="text"
                    className="text-input"
                    value={newStudent.lastName}
                    onChange={(e) => setNewStudent((prev) => ({ ...prev, lastName: e.target.value }))}
                  />
                </div>
              </div>
              <div className="input-group-divided">
                <div>
                  <label>Class*</label>
                  <select
                    className="text-input"
                    required
                    value={newStudent.standard || defaultStandard || ''}
                    onChange={(e) => setNewStudent((prev) => ({ ...prev, standard: e.target.value }))}
                  >
                    <option value=""></option>
                    <option value="kg">KG</option>
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                    <option value="5">5</option>
                    <option value="6">6</option>
                    <option value="7">7</option>
                    <option value="8">8</option>
                    <option value="9">9</option>
                    <option value="10">10</option>
                    <option value="11">11</option>
                    <option value="12">12</option>
                  </select>
                </div>
                <div>
                  <label>Student Gender*</label>
                  <select
                    className="text-input"
                    required
                    value={newStudent.gender}
                    onChange={(e) => setNewStudent((prev) => ({ ...prev, gender: e.target.value }))}
                  >
                    <option value=""></option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              <button
                type="button"
                className="tertiary-btn"
                style={{ paddingLeft: 0 }}
                onClick={() => {
                  setAddNewStudent(false);
                  setNewStudent({ firstName: '', lastName: '', standard: defaultStandard || '', gender: '' });
                }}
              >
                Use existing student
              </button>
            </>
          )}
        </div>
        <div className="modal-footer">
          <input type="button" className="xs-neutral-btn" onClick={() => setModal(false)} value="Cancel" />
          <input
            type="submit"
            className="xs-approve-btn"
            disabled={loading}
            value={loading ? 'Saving...' : 'Link Student'}
          />
        </div>
      </form>
    </Modal>
  );
};

export default LinkStudentModal;
